import {Injectable} from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {HubConnection, IRetryPolicy, LogLevel} from '@microsoft/signalr';
import {AppService} from '../app/app.service';
import {AuthService} from '../../../auth/services/auth/auth.service';
import {SignalRCrudHandler} from './handlers/signalRCrudHandler';
import {SignalRFlagHandler} from './handlers/signalRFlagHandler';
import {SignalRCustomerConvertHandler} from './handlers/signalRCustomerConvertHandler';
import {ApiService} from '../server/api.service';
import {TenantService} from '../../../auth/services/tenant/tenant.service';
import {AppointmentSRHandler} from './handlers/appointmentHandler';
import {ChatHandler} from './handlers/chatHandler';
import {ChatService} from '../../../chat/services/chat.service';
import {UserService} from '../../../user/services/user/user.service';
import {PlatformService} from '../native/platform.service';
import {NotificationSRHandler} from './handlers/notificationHandler';
import {LogSourceType} from "../../../logging/data/logSourceType";
import {LogSource} from "../../../logging/data/logSource";
import {LoggerService} from "../../../logging/services/logger.service";

@Injectable({
  providedIn: 'root'
})
export class SignalRService {

  connection: HubConnection;

  crudHandler: SignalRCrudHandler;
  flagHandler: SignalRFlagHandler;
  customerConvertHandler: SignalRCustomerConvertHandler;
  appointmentHandler: AppointmentSRHandler;
  chatHandler: ChatHandler;
  notificationHandler: NotificationSRHandler;

  constructor(public appService: AppService,
              public serverService: ApiService,
              private tenantService: TenantService,
              private chatService: ChatService,
              private userService: UserService,
              public authService: AuthService,
              private platformService: PlatformService,
              private loggerService: LoggerService) {
    this.appService = appService;
    this.authService = authService;
    this.crudHandler = new SignalRCrudHandler(this);
    this.flagHandler = new SignalRFlagHandler(this);
    this.customerConvertHandler = new SignalRCustomerConvertHandler(this);
    this.appointmentHandler = new AppointmentSRHandler(this);
    this.chatHandler = new ChatHandler(this, this.chatService, this.userService, this.authService, this.tenantService);
    this.notificationHandler = new NotificationSRHandler(this);
  }

  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(this.serverService.applicationApiClient.baseUrl.replace('/api', '') + 'realtime', {
          headers: {
            tenantCode: this.tenantService.tenantDto.tenant.id,
            Authorization: this.authService.jwtToken
          }
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext: any) => {
            if (retryContext.previousRetryCount < 5) {
              return 1000;
            } else {
              return 5000;
            }
          },
          onRetry: (retryContext: any) => {
            this.loggerService.logMessage(0, LogSourceType.SERVICE, LogSource.PlatformService, `SignalR connection lost, attempting to reconnect (retry ${retryContext.previousRetryCount + 1})`);
          },
          onReconnecting: (retryContext: any) => {
            this.loggerService.logMessage(0, LogSourceType.SERVICE, LogSource.PlatformService, `SignalR connection lost, attempting to reconnect (retry ${retryContext.previousRetryCount + 1})`);
          }
        } as IRetryPolicy)
        .configureLogging(LogLevel.Information)
        .build();

      this.crudHandler.hook(this.connection);
      this.flagHandler.hook(this.connection);
      this.customerConvertHandler.hook(this.connection);
      this.appointmentHandler.hook(this.connection);
      this.chatHandler.hook(this.connection);
      this.notificationHandler.hook(this.connection);

      this.connection.on('AuthSuccess', () => {
        if (this.platformService.isMainWindow) {
          this.appService.showSuccessMessage('Logged in to realtime updates');
        }
      });
      this.connection.on('AuthFail', () => {
        this.appService.showErrorMessage('Couldn\'t log in to realtime updates');
      });

      this.connection
        .start()
        .then(() => {
          this.connectionSuccessListener();
          resolve();
        })
        .catch((error) => {
          console.log(`SignalR connection error: ${error}`);
          reject();
        });

      this.connection.onreconnected((connectionId) => {
        this.loggerService.logMessage(0, LogSourceType.SERVICE, LogSource.PlatformService, `SignalR reconnected! connectionId: ${connectionId}`);
        this.connectionSuccessListener();
      });

      this.connection.onclose((error) => {
        console.log(`SignalR connection closed due to error: ${error}`);
        this.loggerService.logMessage(0, LogSourceType.SERVICE, LogSource.PlatformService, `SignalR connection closed due to error: ${error}`);
      });

    });
  }

  connectionSuccessListener() : void
  {
    this.connection.invoke('Register', this.authService.jwtToken, this.tenantService.tenantDto.tenant.id).then(() => {
      console.log(
        `SignalR connection success! connectionId: ${this.connection.connectionId} `
      );
    });
  }

  disconnect(): void {
    this.connection.stop();
  }
}
